import { Box, Button, Chip, Divider } from "@mui/material";
import { grey } from "@mui/material/colors";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { QuickSendContext } from "contexts/QuickSend";
import { QuickSendDrawerContext } from "contexts/QuickSendDrawer";
import { QuickSendErrorContext } from "contexts/QuickSendError";
import { QuickSendOutreachTabsContext } from "contexts/QuickSendOutreachTabs";
import {
  EmailOption,
  QuickSendScheduleContext,
} from "contexts/QuickSendSchedule";
import { useContext, useEffect, useRef } from "react";
import { PreviewMessage } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import {
  DAYS_TO_DELAY_FOLLOWUP,
  MAXIMUM_FOLLOWUP_ALLOWED,
} from "constants/templates";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";

import FollowUpTab from "./FollowUpTab";
import styles from "./styles";

export default function SelectOutreach() {
  const { outreachTab, setOutreachTab } = useContext(
    QuickSendOutreachTabsContext,
  );
  const {
    outreachMessages,
    setOutreachMessages,
    setTemplateForQuickSend,
    bentoBrand,
    handleFetchPreviews,
    bentoContact,
  } = useContext(QuickSendContext);
  const { previewLoading } = useContext(QuickSendDrawerContext);
  const { selectedOption, setSelectedOption, hasScheduled } = useContext(
    QuickSendScheduleContext,
  );

  const followups = outreachMessages.slice(1, outreachMessages?.length);
  const { followupTemplates } = useContext(OutreachTemplatesContext);
  const { errors } = useContext(QuickSendErrorContext);
  const { emailSettings } = useContext(OrganizationUserContext);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const savedDelayForOrder = (emailOrder: number): number => {
    const saved = emailSettings?.emailSettingTemplates?.find(
      (t) => t.emailOrder === emailOrder,
    )?.delayDays;
    return saved ?? DAYS_TO_DELAY_FOLLOWUP;
  };

  useEffect(() => {
    setOutreachMessages((prev: PreviewMessage[]) => {
      let changed = false;
      const next = prev.map((msg, idx) => {
        if (idx === 0) return msg;
        if (msg.delayDays == null) {
          changed = true;
          return { ...msg, delayDays: savedDelayForOrder(idx) };
        }
        return msg;
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outreachMessages.length, emailSettings, setOutreachMessages]);

  const handleAddMoreFollowup = () => {
    if (previewLoading !== -1) return;
    if (followups?.length === MAXIMUM_FOLLOWUP_ALLOWED) return;
    trackEvent("Add Another Follow Up Button Clicked");

    setTemplateForQuickSend((prev) => {
      const copy = makeDeepCopy(prev);
      if (followupTemplates?.length === 0) {
        copy.push({
          name: "Followup Default",
          body: "",
          subject: "Followup Default",
          contentType: TemplateContentType.FOLLOWUP,
        });
      } else {
        copy.push(followupTemplates[0]);
      }
      if (bentoBrand) {
        handleFetchPreviews(bentoBrand.id, bentoContact, copy);
      }
      return copy;
    });

    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
      }
    }, 50);
  };

  const handleRemoveFollowUp = (index: number) => {
    if (previewLoading !== -1) return;
    trackEvent("Remove Follow Up Button Clicked", {
      "Follow Up Position": index,
    });
    setTemplateForQuickSend((prev: Template[]) => {
      const copy = makeDeepCopy(prev);
      copy.splice(index, 1);
      if (bentoBrand) {
        handleFetchPreviews(bentoBrand.id, bentoContact, copy);
      }
      return copy;
    });
  };

  const switchToOutreach = (e: CustomEvent) => {
    if (previewLoading !== -1) return;
    e.preventDefault();
    setOutreachTab(0);
  };

  const switchToFollowUp = (e: CustomEvent, index: number) => {
    if (previewLoading !== -1) return;
    e.preventDefault();
    setOutreachTab(index);
    if (selectedOption === EmailOption.test) {
      setSelectedOption(hasScheduled ? EmailOption.schedule : EmailOption.send);
    }
  };

  const handleDelayChange = (index: number, value: number) => {
    trackEvent("Follow Up Delay Changed", {
      "Follow Up Position": index,
      "Delay Days": value,
      Source: "Live Preview",
    });
    setOutreachMessages((prev: PreviewMessage[]) => {
      const copy = makeDeepCopy(prev);
      if (copy[index]) {
        copy[index] = { ...copy[index], delayDays: value };
      }
      return copy;
    });
  };

  return (
    <>
      <Box
        ref={scrollRef}
        sx={{
          display: "flex",
          alignItems: "end",
          gap: 0.5,
          overflowX: "auto",
          scrollBehavior: "smooth",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Chip
          label="Outreach"
          size="small"
          sx={[
            { flexShrink: 0 },
            outreachTab === 0 ? styles.active : styles.inactive,
            errors.outreachBody === 0 && outreachTab !== 0 && styles.error,
          ]}
          onClick={switchToOutreach}
        />

        {followups?.map((x, index) => {
          const tabIndex = index + 1;
          const delayDays = x.delayDays ?? DAYS_TO_DELAY_FOLLOWUP;
          return (
            <FollowUpTab
              key={index}
              tabIndex={tabIndex}
              days={delayDays}
              sx={[
                outreachTab === tabIndex ? styles.active : styles.inactive,
                errors.outreachBody === tabIndex &&
                  outreachTab !== tabIndex &&
                  styles.error,
              ]}
              onSelect={(e) => switchToFollowUp(e, tabIndex)}
              onDelayChange={(val) => handleDelayChange(tabIndex, val)}
              onRemove={() => handleRemoveFollowUp(tabIndex)}
            />
          );
        })}

        {followups?.length < MAXIMUM_FOLLOWUP_ALLOWED && (
          <Button
            size="small"
            sx={[styles.addButton, { flexShrink: 0 }]}
            onClick={handleAddMoreFollowup}
          >
            {followups?.length > 0 ? "Add + " : "Add Follow-Up +"}
          </Button>
        )}
      </Box>
      <Divider sx={{ mt: -0.1, backgroundColor: grey[200] }} />
    </>
  );
}
